import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextInput } from '@any-ui-react/core';
import { useLens } from '@hookform/lenses';

import {
  AnyChatForm,
  AnyChatMediaUpload,
  SortableCard,
  SortableRoot,
  AddMessageElement,
} from '@anyx/feature-crm/shared';


import { useBroadcastImageUpload } from '../hooks';
import { LineMessageFormSubView, useLineMessageStore } from '../store';
import { ImageTypeBlockFieldErrors, LineMessageFormFieldsProps } from '../types';
import { UrlActionBlock } from '../url-action-block';
import { DEFAULT_IMAGE_TYPE_BLOCK_CARD } from '../utils';

const MAX_IMAGE_TYPE_CARDS = 10;
const MAX_IMAGE_TYPE_LABEL_LENGTH = 12;

const ImageTypeBlockCard = ({
  blockIndex,
  cardIndex,
}: {
  blockIndex: number;
  cardIndex: number;
}) => {
  const { t } = useTranslation('crm');
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);

  const { register, control, unregister, setValue, formState } =
    useFormContext<LineMessageFormFieldsProps>();

  const trackingEnabled = useWatch({
    control,
    name: 'trackingEnabled',
  });

  const imageFiles = useWatch({
    control,
    name: `messages.${blockIndex}.cards.${cardIndex}.image` as const,
  });

  const actionLabel = useWatch({
    control,
    name: `messages.${blockIndex}.cards.${cardIndex}.actionLabel` as const,
  });

  const actionUrlStatus = useWatch({
    control,
    name: `messages.${blockIndex}.cards.${cardIndex}.actionUrl.status` as const,
  });

  const lens = useLens({
    control,
  });

  const mediaUploadProps = useBroadcastImageUpload({
    messageType: 'IMAGES',
    onImageUpdate: (image) => {
      setValue(`messages.${blockIndex}.cards.${cardIndex}.image` as const, image, {
        shouldValidate: formState.submitCount > 0,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  });

  const error = useMemo(() => {
    const errorMsg = formState.errors?.messages?.[blockIndex] as ImageTypeBlockFieldErrors;

    if (errorMsg && 'cards' in errorMsg) {
      return errorMsg.cards?.[cardIndex];
    }

    return undefined;
  }, [blockIndex, formState, cardIndex]);

  useEffect(() => {
    register(`messages.${blockIndex}.cards.${cardIndex}.image`);

    return () => {
      unregister(
        [
          `messages.${blockIndex}.cards.${cardIndex}.image` as const,
          `messages.${blockIndex}.cards.${cardIndex}.actionLabel` as const,
          `messages.${blockIndex}.cards.${cardIndex}.actionUrl` as const,
        ],
        {
          keepDefaultValue: true,
          keepDirty: true,
          keepError: true,
          keepIsValid: true,
          keepTouched: true,
          keepValue: true,
        }
      );
    };
  }, [register, unregister, blockIndex, cardIndex]);

  return (
    <AnyChatForm.Block>
      <AnyChatMediaUpload
        {...mediaUploadProps}
        inputWrapperProps={{
          label: t('crm.lineMessageForm.fields.image.label'),
          required: true,
          error: error?.image?.message,
        }}
        files={imageFiles ? [imageFiles] : []}
        disabled={isFormDisabled}
      />

      <UrlActionBlock
        required
        lens={lens.reflect((_, l) => ({
          status: l.focus(`messages.${blockIndex}.cards.${cardIndex}.actionUrl.status` as const),
          manual: l.focus(`messages.${blockIndex}.cards.${cardIndex}.actionUrl.manual` as const),
          variable: l.focus(
            `messages.${blockIndex}.cards.${cardIndex}.actionUrl.variable` as const
          ),
        }))}
        trackingEnabled={trackingEnabled}
        actionStatus={actionUrlStatus}
      />

      <TextInput
        {...register(`messages.${blockIndex}.cards.${cardIndex}.actionLabel` as const)}
        inputWrapperOrder={['label', 'input', 'error', 'description']}
        classNames={{
          description: 'text-right',
        }}
        label={t('crm.lineMessageForm.fields.actionLabel.label')}
        placeholder={t('crm.lineMessageForm.fields.actionLabel.placeholder')}
        description={
          <>
            <span className="font-semibold">{actionLabel?.length || 0}</span>/
            {MAX_IMAGE_TYPE_LABEL_LENGTH}
          </>
        }
        error={error && 'actionLabel' in error ? error?.actionLabel?.message : undefined}
        maxLength={MAX_IMAGE_TYPE_LABEL_LENGTH}
        readOnly={isFormDisabled}
      />
    </AnyChatForm.Block>
  );
};

export function ImageTypeBlockCards({
  view: { blockIndex, name: blockName },
}: {
  view: LineMessageFormSubView;
}) {
  const { t } = useTranslation('crm');
  const navigateToBlockView = useLineMessageStore((store) => store.navigateToBlockView);
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);

  const { control, formState } = useFormContext<LineMessageFormFieldsProps>();

  const { fields, append, move, remove } = useFieldArray({
    control,
    name: `messages.${blockIndex}.cards` as const,
  });

  const cardsId = useMemo(() => {
    return fields.map(({ id }) => id);
  }, [fields]);

  const cardsErrors = useMemo(() => {
    const error = formState.errors.messages?.[blockIndex] as ImageTypeBlockFieldErrors;

    if (error) {
      return 'cards' in error ? error.cards : undefined;
    }

    return undefined;
  }, [blockIndex, formState]);

  return (
    <AnyChatForm.Block className="overflow-auto">
      <SortableRoot className="space-y-2" itemsId={cardsId} onMove={move}>
        {fields.map((field, fieldIndex) => {
          return (
            <SortableCard
              key={field.id}
              id={field.id}
              render={t('crm.lineMessageForm.card')}
              disabled={isFormDisabled}
              disableRemove={fields.length <= 1}
              hideRemove={isFormDisabled}
              error={!!cardsErrors?.[fieldIndex]}
              deleteTooltip={t('crm.lineMessageForm.actions.deleteCard')}
              onClick={() => {
                navigateToBlockView(blockName, blockIndex, fieldIndex);
              }}
              onRemove={() => {
                remove(fieldIndex);
              }}
            />
          );
        })}
      </SortableRoot>

      {!isFormDisabled && (
        <AddMessageElement
          currentLength={fields.length}
          maxLength={MAX_IMAGE_TYPE_CARDS}
          onAppend={() => {
            append(DEFAULT_IMAGE_TYPE_BLOCK_CARD);
          }}
          label={t('crm.lineMessageForm.actions.addCard')}
        />
      )}
    </AnyChatForm.Block>
  );
}

export function ImageTypeBlock({ view }: { view: LineMessageFormSubView }) {
  const { cardIndex, blockIndex } = view;

  if (cardIndex !== undefined) {
    return <ImageTypeBlockCard blockIndex={blockIndex} cardIndex={cardIndex} />;
  }

  return <ImageTypeBlockCards view={view} />;
}
